<template>
  <div class="px-6 py-4 bg-app-lime rounded-t-xl md:px-16 md:py-8">
    <h4 v-if="title" class="text-2xl uppercase">
      {{ title }}
    </h4>
    <h5 v-if="subtitle" class="text-2xl font-bold leading-tight uppercase">
      {{ subtitle }}
    </h5>
    <div v-if="introduction" class="mb-6" v-html="introduction" />
    <div class="flex flex-col mb-6">
      <div class="grid gap-3 grid-cols-3">
        <AtomButton :button-type="buttonType(price1)" class="mb-2" @click.prevent="setChosenFixedAmount(price1, 1)"> {{ $t('donate-amount') }} &euro; {{ price1 }} </AtomButton>
        <AtomButton v-if="price2" :button-type="buttonType(price2)" class="mb-2" @click.prevent="setChosenFixedAmount(price2, 2)">
          {{ $t('donate-amount') }} &euro; {{ price2 }}
        </AtomButton>
        <AtomButton v-if="price3" :button-type="buttonType(price3)" class="mb-2" @click.prevent="setChosenFixedAmount(price3, 3)">
          {{ $t('donate-amount') }} &euro; {{ price3 }}
        </AtomButton>
      </div>
      <div>
        <AtomButton v-if="!customAmount" button-type="quaternary" @click.prevent="customAmountActivate">{{ $t('donate-different-amount') }}</AtomButton>
        <MoleculeInputValue v-if="customAmount" @value-update="setUserCustomAmount" />
      </div>
    </div>
    <div class="flex items-center">
      <AtomButton button-type="quinary" :full-width="false" class="mb-2 mr-6" @click.prevent="submitForm">
        <AtomIconAppHeart class="mr-3 w-5" />
        {{ $t('donate') }}
      </AtomButton>
      <AtomLink :url="url + getFirstQueryParamConnector() + 'freq=Maandelijks'" class="pb-1 text-xs font-light border-b border-solid border-black cursor-pointer">
        {{ $t('donate-monthly') }}
        <AtomIconArrowRight class="inline-block mb-1 ml-2 w-3 h-3" />
      </AtomLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { getUrlSegments } from '~/composables/util'

interface Props {
  title?: string
  subtitle?: string
  introduction?: string
  image?: object // We cannot get the exact structure here without more information
  url: string
  price1: number
  price2?: number | null
  price3?: number | null
}

const props = defineProps<Props>()

const nuxtApp = useNuxtApp()

const router = useRouter()

const userCustomAmount = ref<number>(0)
const chosenFixedAmount = ref<number>(0)
const chosenIndex = ref<number>(0)
const customAmount = ref<boolean>(false)

const currentHost = nuxtApp.ssrContext?.req?.headers?.host || window?.location?.host || '-'

const getFirstQueryParamConnector = (): string => {
  if (props.url.includes('?')) {
    return '&'
  }
  return '?'
}

const buttonType = (amount: number): string => {
  return amount === chosenFixedAmount.value ? 'tertiary' : 'quaternary'
}

const customAmountActivate = (): void => {
  customAmount.value = true
  chosenFixedAmount.value = 0
}

const setChosenFixedAmount = (amount: number, index: number): void => {
  customAmount.value = false
  chosenFixedAmount.value = amount
  chosenIndex.value = index
}

const setUserCustomAmount = (amount: string): void => {
  userCustomAmount.value = parseFloat(amount)
}

const submitForm = (): void => {
  let url: string | null = null
  const amount = chosenFixedAmount.value || userCustomAmount.value || undefined

  if (amount) {
    url = props.url + getFirstQueryParamConnector() + 'freq=Eenmalig&bedrag=' + 'bedrage' + chosenIndex.value
  }
  if (userCustomAmount.value) {
    url = props.url + getFirstQueryParamConnector() + 'freq=Eenmalig&bedragOpen=' + userCustomAmount.value
  }

  if (url) {
    const { protocol, host, rest } = getUrlSegments(url)
    if ([undefined, 'http:', 'https:'].includes(protocol) && [undefined, currentHost].includes(host)) {
      router.push({
        path: rest,
      })
      return
    }

    window.location.href = url
  }
}
</script>

<style lang="scss" scoped></style>
